import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import apiClient from './api';
import { API_ENDPOINTS } from './config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    // R10e35b-6-007: Clear jurisdiction_id on logout
    localStorage.removeItem('jurisdiction_id');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const response = await apiClient.get(API_ENDPOINTS.AUTH.ME);
        // C9c (2026-09-26): re-seed jurisdiction_id on every page load; some sign-in
        // paths (MFA enrollment) never stored it, which blocked setup screens.
        if (response.data?.jurisdiction_id && !localStorage.getItem('jurisdiction_id')) {
          localStorage.setItem('jurisdiction_id', response.data.jurisdiction_id);
        }
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        // Don't set sessionExpired here - this is initial load, not an expired session
        logout();
      }
    }
    setLoading(false);
  }, [logout]);

  // Set up axios interceptor to detect 401 responses when authenticated
  useEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        // If we get a 401 while authenticated, the session has expired
        if (error.response?.status === 401 && isAuthenticated) {
          setSessionExpired(true);
          logout();
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [isAuthenticated, logout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // E-i.1 (2026-09-23): `entry` = 'admin' only from the unlisted SystemAdmin sign-in route.
  const login = async (email, password, mfaToken = null, entry = undefined) => {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, {
      email,
      password,
      mfa_token: mfaToken,
      ...(entry ? { entry } : {}),
    });

    // R10e36.69.1-F503-R2: Check if MFA setup is required
    if (response.data.mfa_setup_required) {
      // Store the limited token for MFA enrollment
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (response.data.user?.jurisdiction_id) {  // C9c
        localStorage.setItem('jurisdiction_id', response.data.user.jurisdiction_id);
      }
      setUser(response.data.user);
      // R10e36.69.1.1-F503-R4: Mark as authenticated (with limited scope)
      // User passed password check, so they ARE authenticated - just scope-limited
      setIsAuthenticated(true);
      return { mfa_setup_required: true, user: response.data.user, scope_limited: true };
    }

    // R10e36.69.1.3-F503-R6: Store mfa_pending token for /mfa/verify page
    if (response.data.requires_mfa) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      // Don't set isAuthenticated - user still needs to verify MFA
      return { requires_mfa: true, user: response.data.user };
    }

    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    // R10e35b-6-007: Store jurisdiction_id for year-setup and other components
    if (response.data.user?.jurisdiction_id) {
      localStorage.setItem('jurisdiction_id', response.data.user.jurisdiction_id);
    }
    setUser(response.data.user);
    setIsAuthenticated(true);
    setSessionExpired(false); // Clear session expired flag on successful login
    return { success: true, user: response.data.user };
  };

  // R10e36.69.1.3-F503-R6: Verify MFA code (TOTP or backup code)
  // R10e36.69.1.4-F503-R7: Remove /api prefix — apiClient baseURL already includes it
  const verifyMfa = async (code) => {
    const response = await apiClient.post('/auth/mfa/verify', { code });
    
    if (response.data.success) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (response.data.user?.jurisdiction_id) {
        localStorage.setItem('jurisdiction_id', response.data.user.jurisdiction_id);
      }
      setUser(response.data.user);
      setIsAuthenticated(true);
      setSessionExpired(false);
      return { success: true, user: response.data.user };
    }
    
    return { success: false };
  };

  const register = async (userData) => {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.REGISTER, userData);
    return response.data;
  };

  // Allow clearing the session expired flag (e.g., after user acknowledges)
  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  // PC119: Refetch /auth/me on demand so Settings can refresh the user object
  // after a profile save without forcing a page reload. Silent — swallows
  // network errors since a stale context is preferable to a session bounce.
  const refreshUser = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.AUTH.ME);
      setUser(response.data);
      // Keep localStorage in sync so a hard reload picks up the new values.
      try { localStorage.setItem('user', JSON.stringify(response.data)); } catch (_) {}
      return response.data;
    } catch (e) {
      console.warn('refreshUser failed (non-fatal):', e);
      return null;
    }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  // Only recalculates when user, loading, isAuthenticated, or sessionExpired actually change
  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated,
    sessionExpired,
    login,
    register,
    logout,
    verifyMfa,  // R10e36.69.1.3-F503-R6
    clearSessionExpired,
    refreshUser,  // PC119
  }), [user, loading, isAuthenticated, sessionExpired, clearSessionExpired, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
